import { useCallback, useState } from 'react';


/**
 * A single selection persisted in localStorage under a key scoped by `scopeId`
 * (e.g. per user or per seller), so two scopes on one browser never inherit each
 * other's choice. Returns `[value, select]`.
 *
 * - Lazily initialised from storage; when `scopeId` changes the value re-reads
 *   for the new scope during render (React's setState-in-render pattern, not an
 *   effect) so consumers never see a stale selection.
 * - `select(next)` updates the value and best-effort persists `String(next)`;
 *   storage failures (private mode / SSR) are swallowed and fall back to
 *   `fallback` on read.
 *
 * Shared by ActiveOrgContext (membership id) and LocationContext (location id).
 */
function useScopedPersistedSelection(prefix, scopeId, fallback = null) {
  const storageKey = useCallback((id) => `${prefix}${id || ''}`, [prefix]);

  const read = useCallback((id) => {
    if (!id) {
      return fallback;
    }
    try {
      return window.localStorage.getItem(storageKey(id)) || fallback;
    }
    catch {
      // Storage unavailable (private mode / SSR) — no persisted selection.
      return fallback;
    }
  }, [fallback, storageKey]);

  const [value, setValue] = useState(() => read(scopeId));
  const [scope, setScope] = useState(scopeId);

  // Re-read persistence when the scope changes (adjust state during render — the
  // recommended alternative to a sync effect).
  if (scope !== scopeId) {
    setScope(scopeId);
    setValue(read(scopeId));
  }

  const select = useCallback((next) => {
    setValue(next);
    try {
      if (scopeId) {
        window.localStorage.setItem(storageKey(scopeId), String(next));
      }
    }
    catch {
      // Persisting is best-effort.
    }
  }, [scopeId, storageKey]);

  return [value, select];
}

export default useScopedPersistedSelection;
